import { useEffect, useState } from 'react';
import { devices, schedules, type Device, type Schedule } from '../../api';

export function DevicesPage() {
  const [list, setList] = useState<Device[]>([]);
  const [schedList, setSchedList] = useState<Schedule[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [pairing, setPairing] = useState<Device | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    try {
      const [d, s] = await Promise.all([devices.list(), schedules.list()]);
      setList(d.devices);
      setSchedList(s.schedules);
    } catch (e) {
      setErr('Failed to load devices');
    }
  }
  useEffect(() => {
    refresh();
    // Always poll every 3s while the devices page is open. Lightweight query
    // and we want quick sync visibility (the agent itself polls every 60s, so
    // download windows can be brief).
    const t = setInterval(refresh, 3000);
    return () => clearInterval(t);
  }, []);

  async function assign(deviceId: string, scheduleId: string) {
    await devices.assign(deviceId, scheduleId || null);
    refresh();
  }

  async function remove(deviceId: string) {
    if (!confirm('Delete this device? The Pi will need to re-pair.')) return;
    await devices.remove(deviceId);
    refresh();
  }

  return (
    <div>
      <div className="toolbar">
        <h1>Devices</h1>
        <div className="spacer" />
        <button className="primary" onClick={() => setShowCreate(true)}>+ Register device</button>
      </div>
      {err && <div className="error">{err}</div>}

      {list.length === 0 ? (
        <div className="empty">No devices yet. Click <strong>Register device</strong> to create a pairing code.</div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th><th>Status</th><th>Last seen</th><th>Hardware ID</th><th>Schedule</th><th>Playlist</th><th></th>
            </tr>
          </thead>
          <tbody>
            {list.map((d) => (
              <tr key={d.id}>
                <td>
                  <strong>{d.name}</strong>
                  {d.status === 'pending' && d.pairing_code && (
                    <button style={{ marginLeft: '0.6em', fontSize: '0.85em' }} onClick={() => setPairing(d)}>
                      Show pairing code
                    </button>
                  )}
                </td>
                <td><StatusBadge device={d} /></td>
                <td>{d.last_seen_at ? new Date(d.last_seen_at).toLocaleString() : '—'}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85em', color: 'var(--fg-3)' }}>
                  {d.hardware_id.startsWith('pending-') ? '—' : d.hardware_id}
                </td>
                <td>
                  <select
                    value={d.schedule_id ?? ''}
                    onChange={(e) => assign(d.id, e.target.value)}
                    style={{ minWidth: 160 }}
                  >
                    <option value="">(none)</option>
                    {schedList.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </td>
                <td style={{ fontSize: '0.9em' }}>
                  {d.playlist_name ? (
                    <span>{d.playlist_name}</span>
                  ) : d.schedule_id ? (
                    <span style={{ color: 'var(--fg-3)', fontStyle: 'italic' }}>(no default playlist)</span>
                  ) : (
                    <span style={{ color: 'var(--fg-3)' }}>—</span>
                  )}
                </td>
                <td><button className="danger" onClick={() => remove(d.id)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && (
        <CreateDeviceModal
          onClose={() => setShowCreate(false)}
          onCreated={(d) => { setShowCreate(false); setPairing(d); refresh(); }}
        />
      )}
      {pairing && <PairingModal device={pairing} onClose={() => setPairing(null)} />}
    </div>
  );
}

function StatusBadge({ device }: { device: Device }) {
  const sync = device.sync_status;
  if (sync?.state === 'syncing') {
    const pct = sync.percent ?? 0;
    const done = sync.bytes_done ?? 0;
    const total = sync.bytes_total ?? 0;
    return (
      <div style={{ minWidth: 160 }}>
        <span className="badge pending" style={{ background: '#1d4ed8', color: '#fff' }}>syncing</span>
        <div style={{
          marginTop: 4, height: 4, background: 'var(--bg-2)',
          borderRadius: 2, overflow: 'hidden', width: 160,
        }}>
          <div style={{
            height: '100%', width: `${pct}%`,
            background: '#3b82f6', transition: 'width 200ms ease-out',
          }} />
        </div>
        <div style={{ fontSize: '0.72em', color: 'var(--fg-3)', marginTop: 2 }}>
          {pct}% &middot; {formatMB(done)} / {formatMB(total)}
        </div>
      </div>
    );
  }
  if (sync?.state === 'recently_completed') {
    return (
      <div style={{ minWidth: 160 }}>
        <span className="badge active" style={{ background: '#059669', color: '#fff' }}>✓ sync complete</span>
        <div style={{ fontSize: '0.72em', color: 'var(--fg-3)', marginTop: 2 }}>
          {formatMB(sync.bytes_total)} just now
        </div>
      </div>
    );
  }
  if (sync?.state === 'recently_failed') {
    return (
      <div style={{ minWidth: 160 }}>
        <span className="badge offline" style={{ background: '#b91c1c', color: '#fff' }}>sync failed</span>
        <div style={{ fontSize: '0.72em', color: 'var(--fg-3)', marginTop: 2 }} title={sync.reason}>
          {sync.reason}
        </div>
      </div>
    );
  }
  if (device.status === 'pending') return <span className="badge pending">pending pairing</span>;
  if (!device.last_seen_at) return <span className="badge offline">never seen</span>;
  const age = Date.now() - new Date(device.last_seen_at).getTime();
  if (age < 60_000) return <span className="badge active">online</span>;
  if (age < 5 * 60_000) return <span className="badge pending">stale</span>;
  return <span className="badge offline">offline</span>;
}

function formatMB(b: number): string {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function CreateDeviceModal({ onClose, onCreated }: { onClose: () => void; onCreated: (d: Device) => void }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit() {
    setBusy(true);
    try {
      const r = await devices.create({ name });
      onCreated(r.device);
    } catch {
      setErr('Failed to create device');
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>Register a new device</h2>
        <div className="field"><label>Device name (e.g. Lobby Screen)</label>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} /></div>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end', marginTop: '1em' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={!name || busy} onClick={submit}>
            {busy ? 'Creating...' : 'Create & show pairing code'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PairingModal({ device, onClose }: { device: Device; onClose: () => void }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ minWidth: 460 }}>
        <h2 style={{ marginTop: 0 }}>Pairing code for {device.name}</h2>
        <div className="pairing-code">{device.pairing_code}</div>
        <p style={{ color: 'var(--fg-2)', lineHeight: 1.6 }}>
          On the Raspberry Pi, place a file at <code>/boot/iotech-pairing.txt</code> containing:
        </p>
        <pre style={{ background: 'var(--bg-2)', padding: '0.9em', borderRadius: 6, fontSize: '0.9em' }}>
{`tenant_slug=YOUR_TENANT_SLUG
pairing_code=${device.pairing_code}`}
        </pre>
        <p style={{ color: 'var(--fg-3)', fontSize: '0.85em' }}>
          Boot the Pi with the prepared SD card. It will claim this code, mark itself active, and start
          calling home. The code is single-use and expires once claimed.
        </p>
        <div style={{ textAlign: 'right' }}><button onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}
